import {
  useInfiniteQuery,
  InfiniteData,
  UseInfiniteQueryOptions,
  keepPreviousData,
  useMutation,
  useQueryClient,
  UseMutationOptions,
} from '@tanstack/react-query';
import { api, TicketFileListDto } from '@sib/api';
import { CACHE_TIME, DEFAULT_PAGE } from '@sib/api/constants';
import { QueryKey } from '@sib/api/enums';
import { InferSearchParamsQueryTypeWithArraySorter } from '@sib/api/types';

export const useInfiniteTicketsFiles = (
  ticketId: string,
  payload: InferSearchParamsQueryTypeWithArraySorter<
    typeof api.tickets.ticketFilesControllerGetTicketFiles,
    1
  >,
  options?: Partial<
    UseInfiniteQueryOptions<
      TicketFileListDto,
      Error,
      InfiniteData<TicketFileListDto>
    >
  >
) =>
  useInfiniteQuery({
    ...options,
    queryKey: [
      QueryKey.Resource.TicketFiles,
      ticketId,
      QueryKey.Operation.List,
      payload,
      QueryKey.Operation.Infinite,
    ],

    queryFn: async ({ pageParam }) => {
      const data = await api.tickets.ticketFilesControllerGetTicketFiles(
        ticketId,
        {
          ...payload,
          page: pageParam as number,
        }
      );

      return { ...data, newItems: 0 };
    },

    getNextPageParam: lastPage => {
      const { hasNextPage } = lastPage;
      const nextPage = lastPage.page + 1;

      return hasNextPage ? nextPage : undefined;
    },
    placeholderData: keepPreviousData,
    initialPageParam: payload?.page || DEFAULT_PAGE,
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
  });

export enum MoveFileDirection {
  AddToDocsRepo = 'add-to-docs-repo',
  RemoveFromDocsRepo = 'remove-from-docs-repo',
}

export interface MoveFileToDocsRepoPayload {
  fileId: string;
  direction: MoveFileDirection;
}

export const useAddTicketFileToDocsRepo = (
  ticketId: string,
  options?: UseMutationOptions<string, Error, MoveFileToDocsRepoPayload>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,

    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.TicketFiles,
          ticketId,
          QueryKey.Operation.List,
        ],
      });

      options?.onSuccess?.(...args);
    },

    mutationFn: ({ fileId, direction }) =>
      direction === MoveFileDirection.AddToDocsRepo
        ? api.tickets.ticketFilesControllerMoveToDocumentRepo(ticketId, fileId)
        : api.tickets.ticketFilesControllerRemoveFromDocumentRepo(
            ticketId,
            fileId
          ),
  });
};

interface TicketFileDeletePayload {
  ticketId: string;
  fileId: string;
}

export const useTicketFileDelete = (
  options?: UseMutationOptions<unknown, Error, TicketFileDeletePayload>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onSuccess: (...args) => {
      const variables = args[1];

      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.TicketFiles,
          variables.ticketId,
          QueryKey.Operation.List,
        ],
      });

      options?.onSuccess?.(...args);
    },
    mutationFn: async ({ ticketId, fileId }) =>
      await api.tickets.ticketFilesControllerDeleteTicketFile(ticketId, fileId),
  });
};
